import { useLanguage } from "@/components/language-provider";
import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2, MailCheck, KeyRound } from "lucide-react";
import { BrandLogo } from "@/components/brand-logo";
import { API_BASE_URL } from "@/lib/config";

export default function ForgotPassword() {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      // Always show the neutral success state (anti enumeration).
      setSent(true);
    } catch {
      toast({ title: t("common.error"), description: t("ui.could_not_reach_the_server_try_again"), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-muted/30">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-4">
            <BrandLogo />
          </div>
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            {sent ? <MailCheck className="w-6 h-6 text-primary" /> : <KeyRound className="w-6 h-6 text-primary" />}
          </div>
          <CardTitle>{sent ? t("auth.checkEmail") : t("auth.forgotTitle")}</CardTitle>
          <CardDescription>
            {sent
              ? t("auth.resetSentDesc")
              : t("auth.forgotDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground text-center">
                {t("ui.didn_t_get_it_check_the_spam_folder_or_try_again")}
              </p>
              <Link href="/login">
                <Button variant="outline" className="w-full" data-testid="button-back-login">{t("ui.back_to_login")}</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t("ui.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="input-forgot-email"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="button-send-reset">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("auth.sendResetLink")}
              </Button>
              <p className="text-sm text-center text-muted-foreground">
                {t("auth.remembered")}{" "}
                <Link href="/login" className="text-primary hover:underline">{t("ui.sign_in_183")}</Link>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
